import React, {useCallback, useMemo} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {SMCCheckbox} from '../../../../../components/Simple/SMCCheckbox';
import {useTranslation} from 'react-i18next';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import EmptyRow from '../../../../../components/Simple/EmptyRow';
import Indentation from '../../../../../components/Simple/Indentation';
import {SMCSelect, SMCOption} from '../../../../../components/Simple/SMCSelect';
import {SMCRadioGroup, SMCRadio} from '../../../../../components/Simple/SMCRadio';
import {TimezoneValues} from '../../../../../api/DBSchema/Timezone';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.reports;
const pageId = pageSchema.pageId;

function BaseReports({
    pageData,
    setObjectData,
    pageDisabled,
    isSMEReportEnabled,
    isReportConfirmationEnabled
}) {
    const { t } = useTranslation();
    
    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);

    const reportsType                = pageData[pageSchema.reportsType];
    const reportID                   = pageData[pageSchema.reportID];
    const reportIDValues             = pageData[pageSchema.reportIDValues];
    const reportsDaily               = pageData[pageSchema.reportsDaily];
    const reportsWeekly              = pageData[pageSchema.reportsWeekly];
    const reportsMonthly             = pageData[pageSchema.reportsMonthly];
    const reportsLang                = pageData[pageSchema.reportsLang];
    const reportAsHtmlBody           = pageData[pageSchema.reportAsHtmlBody];
    const reportsTimezone            = pageData[pageSchema.reportsTimezone];
    const reportsConfirmationEnabled = pageData[pageSchema.reportsConfirmationEnabled];
    
    const reportsTypeValues          = pageSchema.reportsTypeValues;
    const reportsLangValues          = pageSchema.reportsLangValues;

    const dayOfWeekValues           = pageSchema.dayOfWeekValues;
    const dayOfMonth                = pageData[pageSchema.dayOfMonth];
    const dayOfWeek                 = pageData[pageSchema.dayOfWeek];

    const dayOfMonthOptions = useMemo(() => (
        [...Array(31)].map((e,i) => (
            <SMCOption key={i+1} value={i+1}>{i+1}</SMCOption>
        ))
    ), [])

    const dayOfWeekOptions = useMemo(() => (
        <>
        <SMCOption value={dayOfWeekValues.monday}>{t('SMP.MONDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.tuesday}>{t('SMP.TUESDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.wednesday}>{t('SMP.WEDNESDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.thursday}>{t('SMP.THURSDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.friday}>{t('SMP.FRIDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.saturday}>{t('SMP.SATURDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.sunday}>{t('SMP.SUNDAY')}</SMCOption>
        </>
    ), [dayOfWeekValues, t]);

    return (
        <EditorLayout>
            {
                isSMEReportEnabled && (
                    <>
                    <SMCLabel bold>{t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONTENT')}</SMCLabel>
                    {/* Reports type */}
                    <SMCRadioGroup
                        disabled={pageDisabled}
                        value={reportsType}
                        onChange={e => {
                            setPageValue(pageSchema.reportsType, e.target.value);
                            setPageValue(pageSchema.reportsLang, reportsLangValues.english); /* Default for language is English when changing report type */
                        }}
                    >
                        <Indentation>
                            <SMCRadio value={reportsTypeValues.gateway} label={t('SMP.PLAN.SERVICES.REPORTS.CLASSIC_REPORT')} />
                            <SMCRadio value={reportsTypeValues.smartEvent} label={t('SMP.PLAN.SERVICES.REPORTS.EXTENDED_REPORT')} />
                            <Indentation>
                                {/* Report ID options are received from SMP via smpDynamics.properties, and this should be synced to there */}
                                <SMCSelect
                                    disabled={reportsType !== reportsTypeValues.smartEvent}
                                    value={reportID}
                                    onChange={e => setPageValue(pageSchema.reportID, e.target.value) }
                                    label={t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONTENT')+':'}
                                >
                                    {Array.isArray(reportIDValues) && reportIDValues.includes('SME_SECURITY_REPORT') && (
                                        <SMCOption value="SME_SECURITY_REPORT">{t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONTENT.COMPLETE_REPORT')}</SMCOption>
                                    )}
                                    {Array.isArray(reportIDValues) && reportIDValues.includes('SME_TP_REPORT') && (
                                        <SMCOption value="SME_TP_REPORT">{t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONTENT.THREAT_PREVENTION')}</SMCOption>
                                    )}
                                    {Array.isArray(reportIDValues) && reportIDValues.includes('SME_SECURITY_OVERVIEW_REPORT') && (
                                        <SMCOption value="SME_SECURITY_OVERVIEW_REPORT">Security Overview</SMCOption>
                                    )}
                                </SMCSelect>
                            </Indentation>
                        </Indentation>
                    </SMCRadioGroup>
                    </>
                )
            }
            <SMCLabel bold>{t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONTENT')}</SMCLabel>
            <Indentation>
                {/* Embeded in email body */}
                <SMCToggleSwitch
                    disabled={pageDisabled || reportsType !== reportsTypeValues.gateway}
                    checked={reportAsHtmlBody}
                    onChange={checked => setPageValue(pageSchema.reportAsHtmlBody, checked)}
                    label={t('SMP.PLAN.SERVICES.REPORTS.LINE1')}
                />
                {/* Language */}
                <SMCSelect
                    label={t('SMP.PLAN.SERVICES.REPORTS.REPORTS_LANG')}
                    value={reportsLang}
                    onChange={e => setPageValue(pageSchema.reportsLang, e.target.value)}
                    disabled={pageDisabled}
                >
                    <SMCOption value={reportsLangValues.english}>{t('SMP.PLAN.SERVICES.REPORTS.REPORTS_LANG.ENGLISH')}</SMCOption>
                    <SMCOption value={reportsLangValues.japanese}>{t('SMP.PLAN.SERVICES.REPORTS.REPORTS_LANG.JAPANESE')}</SMCOption>
                    {
                        // These languages are only available on gateway reports, not on extened reports
                        reportsType === reportsTypeValues.gateway && (
                            <>
                            <SMCOption value={reportsLangValues.hebrew}>{t('SMP.PLAN.SERVICES.REPORTS.REPORTS_LANG.HEBREW')}</SMCOption>
                            <SMCOption value={reportsLangValues.german}>{t('SMP.PLAN.SERVICES.REPORTS.REPORTS_LANG.GERMAN')}</SMCOption>
                            </>
                        )
                    }
                </SMCSelect>
                {
                    isSMEReportEnabled && (
                        <SMCSelect
                            disabled={pageDisabled || reportsType !== reportsTypeValues.smartEvent}
                            value={reportsTimezone}
                            onChange={e => setPageValue(pageSchema.reportsTimezone, e.target.value)}
                            label={t('SMP.PLAN.SERVICES.REPORTS.REPORTS_TIME_ZONE')}
                            tooltip={t('SMP.PLAN.SERVICES.REPORTS.REPORTS_TIME_ZONE.TOOLTIP')}
                            showEmptyOptionOnDisabled={true}
                        >
                        {
                            TimezoneValues.map((timezoneConf) => (
                                <SMCOption key={timezoneConf['location']} value={timezoneConf['location']}>
                                    {t(timezoneConf['i18n'])}
                                </SMCOption>
                            ))
                        }
                        </SMCSelect>
                    )
                }
            </Indentation>
            
            <EmptyRow />

            <SMCLabel bold>{t('SMP.PLAN.SERVICES.REPORTS.PB_SETTINGS')}</SMCLabel>
            <Indentation>
                <SMCCheckbox
                    disabled={pageDisabled}
                    checked={reportsDaily}
                    onChange={e => setPageValue(pageSchema.reportsDaily, e.target.checked)}
                    label={t('SMP.PLAN.SERVICES.REPORTS.SEND_DAILY')}
                />
                <SMCCheckbox
                    disabled={pageDisabled}
                    checked={reportsWeekly}
                    onChange={e => setPageValue(pageSchema.reportsWeekly, e.target.checked)}
                    label={t('SMP.PLAN.SERVICES.REPORTS.SEND_WEEKLY')}
                />
                    {
                    reportsWeekly &&
                    <>
                        <Indentation>
                            <SMCSelect
                                label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_WEEK')}
                                tooltip={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_WEEK') +' '+ t('SMP.PLAN.SEC_SOFT_BLADES.RUNNING') + ' R80.20.40 '+ t('SMP.PLAN.SEC_SOFT_BLADES.HIGHER')+ '.'}
                                value={dayOfWeek}
                                onChange={e => setPageValue(pageSchema.dayOfWeek, e.target.value)}
                                disabled={pageDisabled}
                            >
                                { dayOfWeekOptions }
                            </SMCSelect>
                        </Indentation>
                    </>
                }
                <SMCCheckbox
                    disabled={pageDisabled}
                    checked={reportsMonthly}
                    onChange={e => setPageValue(pageSchema.reportsMonthly, e.target.checked)}
                    label={t('SMP.PLAN.SERVICES.REPORTS.SEND_MONTHLY')}
                />
                 {
                    reportsMonthly &&
                    <>
                        <Indentation>
                            <SMCSelect 
                                label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_MONTH')} 
                                tooltip={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_MONTH') +' '+ t('SMP.PLAN.SEC_SOFT_BLADES.RUNNING') + ' R80.20.40 '+ t('SMP.PLAN.SEC_SOFT_BLADES.HIGHER')+ '.'} 
                                value={dayOfMonth} 
                                onChange={e => setPageValue(pageSchema.dayOfMonth, e.target.value)}
                                disabled={pageDisabled}
                            >
                                { dayOfMonthOptions }
                            </SMCSelect>
                        </Indentation>
                    </>
                }
            </Indentation>
            {
                isReportConfirmationEnabled && (
                    <>
                    <SMCLabel bold>{t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONFIRMATION')}</SMCLabel>
                    <Indentation>
                        <SMCCheckbox
                            checked={reportsConfirmationEnabled}
                            onChange={e => setPageValue(pageSchema.reportsConfirmationEnabled, e.target.checked)}
                            label={t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONFIRMATION_ENABLE')}
                            tooltip={
                                <>
                                {t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONFIRMATION_ENABLE_TOOLTIP.LINE1')}<br />
                                {t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONFIRMATION_ENABLE_TOOLTIP.LINE2')}
                                <b>{t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONFIRMATION_ENABLE_TOOLTIP.LINE3')}</b>
                                {t('SMP.PLAN.SERVICES.REPORTS.REPORT_CONFIRMATION_ENABLE_TOOLTIP.LINE4')}
                                </>
                            }
                        />
                    </Indentation>
                    </>
                )
            }
        </EditorLayout>
    );
}

export default BaseReports;